import {
  Actions,
  Button,
  ButtonIcon,
  Dialog,
  IconDelete,
  IconEdit,
  IconVisibility,
  Spread,
  Stack,
  Text,
} from 'braid-design-system';
import React from 'react';

import type { EventInput, EventRecord } from '#shared/types';
import { validateEvent } from '#shared/validateEvent';
import { presignUpload } from '#src/api/communityDirectory';
import { resizeImageFile, uploadToPresignedPost } from '#src/utils/imageResize';

import { DialogErrorNotice } from '../DialogErrorNotice';
import { EventFields } from './EventFields';
import {
  eventToFieldValues,
  toEventInput,
  type EventFieldValues,
} from './eventFieldUpdates';

type EventDetailProps = {
  event: EventRecord;
  onSave: (input: EventInput) => Promise<void>;
  onPublish?: (input: EventInput) => Promise<void>;
  onUnpublish?: () => Promise<void>;
  onDelete?: () => Promise<void>;
};

export const EventDetail = ({
  event,
  onSave,
  onPublish,
  onUnpublish,
  onDelete,
}: EventDetailProps) => {
  const [open, setOpen] = React.useState(false);
  const [fields, setFields] = React.useState<EventFieldValues>(() =>
    eventToFieldValues(event),
  );
  const [imageFile, setImageFile] = React.useState<File | null>(null);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setFields(eventToFieldValues(event));
      setImageFile(null);
      setErrors({});
    }
  }, [event, open]);

  const handleFieldChange = (
    field: Exclude<keyof EventFieldValues, 'dates'>,
    value: string | boolean,
  ) => {
    setFields((current) => ({ ...current, [field]: value }));
  };

  const handleDatesChange = (dates: string[]) => {
    setFields((current) => ({ ...current, dates }));
  };

  const uploadImageIfNeeded = async (): Promise<string | undefined> => {
    if (!imageFile) {
      return event.imageKey;
    }

    const { blob, contentType } = await resizeImageFile(imageFile);
    const presigned = await presignUpload(contentType);
    await uploadToPresignedPost(
      presigned.url,
      presigned.fields,
      blob,
      contentType,
    );
    return presigned.key;
  };

  const runAction = async (
    action: (input: EventInput) => Promise<void>,
    validationOptions: { requireDate?: boolean } = {},
    closeOnSuccess = true,
  ) => {
    setErrors({});

    const validation = validateEvent(toEventInput(fields), {
      requireDescription: false,
      ...validationOptions,
    });
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    setSaving(true);
    try {
      const imageKey = await uploadImageIfNeeded();
      await action(toEventInput(fields, imageKey));
      setImageFile(null);
      if (closeOnSuccess) {
        setOpen(false);
      }
    } catch (err) {
      if (err && typeof err === 'object' && 'errors' in err) {
        setErrors((err as { errors: Record<string, string> }).errors);
      } else {
        setErrors({
          _form: err instanceof Error ? err.message : 'Something went wrong.',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const runWithoutValidation = async (action: () => Promise<void>) => {
    setErrors({});
    setSaving(true);
    try {
      await action();
      setOpen(false);
    } catch (err) {
      setErrors({
        _form: err instanceof Error ? err.message : 'Something went wrong.',
      });
    } finally {
      setSaving(false);
    }
  };

  const statusLabel = event.status === 'published' ? 'Published' : 'Draft';

  return (
    <>
      <Spread direction="horizontal" space="small" alignY="center">
        <Stack space="xsmall">
          <Text weight="strong">{event.title || 'Untitled event'}</Text>
          <Text tone="secondary">
            {statusLabel}
            {event.updatedAt
              ? ` · Updated ${new Date(event.updatedAt).toLocaleString()}`
              : null}
          </Text>
        </Stack>
        <Actions>
          <ButtonIcon icon={<IconEdit />} variant="soft" onClick={() => setOpen(true)} label="Edit event" />
          {onUnpublish ? (
            <ButtonIcon
              icon={<IconVisibility hidden />}
              variant="soft"
              tone="brandAccent"
              onClick={() => {
                void onUnpublish().catch(() => undefined);
              }}
              label="Unpublish event"
            />
          ) : null}
          {onDelete ? (
            <ButtonIcon
              icon={<IconDelete />}
              variant="soft"
              tone="brandAccent"
              onClick={() => {
                void onDelete().catch(() => undefined);
              }}
              label="Delete event"
            />
          ) : null}
        </Actions>
      </Spread>

      <Dialog
        id={`edit-event-${event.id}`}
        title="Edit event"
        open={open}
        onClose={() => {
          if (!saving) {
            setOpen(false);
          }
        }}
        width="medium"
      >
        <Stack space="medium">
          <DialogErrorNotice message={errors._form} />

          <Text tone="secondary">
            {statusLabel}
            {event.updatedAt
              ? ` · Updated ${new Date(event.updatedAt).toLocaleString()}`
              : null}
          </Text>

          <EventFields
            eventId={event.id}
            fields={fields}
            errors={errors}
            onChange={handleFieldChange}
            onDatesChange={handleDatesChange}
          />

          <Stack space="small">
            <Text weight="strong">Event image</Text>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => setImageFile(e.currentTarget.files?.[0] ?? null)}
            />
            <Text tone="secondary">
              JPEG, PNG or WebP, up to 5 MB
              {event.imageKey ? ' · Image attached' : ''}
            </Text>
          </Stack>

          <Actions>
            <Button loading={saving} onClick={() => runAction(onSave)}>
              Save changes
            </Button>
            {onPublish ? (
              <Button
                tone="formAccent"
                loading={saving}
                onClick={() => runAction(onPublish, { requireDate: true })}
              >
                Save & publish
              </Button>
            ) : null}
            {onUnpublish ? (
              <Button
                variant="ghost"
                loading={saving}
                onClick={() => runWithoutValidation(onUnpublish)}
              >
                Unpublish
              </Button>
            ) : null}
            {onDelete ? (
              <Button
                tone="critical"
                variant="ghost"
                onClick={() => runWithoutValidation(onDelete)}
              >
                Delete draft
              </Button>
            ) : null}
            <Button
              variant="transparent"
              onClick={() => setOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
          </Actions>
        </Stack>
      </Dialog>
    </>
  );
};
